import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Organization } from '@/types/organization'
import { storage } from '@/firebase/config'
import { 
  collection,
  doc, 
  getDoc,
  updateDoc,
  query,
  where,
  getDocs
} from 'firebase/firestore'
import { db } from '@/firebase/config'

export const useCurrentOrganizationStore = defineStore('currentOrganization', () => {
  const currentOrganization = ref<Organization | null>(null)
  const defaultOrganizationId = ref<string | null>(null)
  const isLoading = ref(false)

  // Dados específicos da organização atual
  const costCenters = ref<any[]>([])
  const customers = ref<any[]>([])
  const suppliers = ref<any[]>([])
  const banks = ref<any[]>([])
  const cashFlow = ref<any[]>([])
  const accountsPayable = ref<any[]>([])
  const accountsReceivable = ref<any[]>([])
  const dashboardData = ref<any>(null)

  // Getters
  const organizationId = computed(() => currentOrganization.value?.id || null)

  // Actions
  const setCurrentOrganization = async (org: Organization) => {
    isLoading.value = true
    try {
      currentOrganization.value = org
      
      // Salva a organização atual e a rota no localStorage
      localStorage.setItem('currentOrganizationId', org.id)
      localStorage.setItem('lastRoute', window.location.pathname)
      
      // Primeiro carregamos os dados básicos
      await Promise.all([
        loadCostCenters(),
        loadCustomers(),
        loadSuppliers(),
        loadBanks()
      ])

      // Depois carregamos os dados que dependem dos dados básicos
      await Promise.all([
        loadAccountsPayable(),
        loadAccountsReceivable()
      ])

      // Por fim carregamos o dashboard que depende de todos os dados
      await loadCashFlow()
      await loadDashboard()
    } catch (error) {
      console.error('Error setting current organization:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const setDefaultOrganization = async (orgId: string) => {
    try {
      defaultOrganizationId.value = orgId
      localStorage.setItem('defaultOrganizationId', orgId)
    } catch (error) {
      console.error('Error setting default organization:', error)
      throw error
    }
  }

  const removeDefaultOrganization = async () => {
    try {
      defaultOrganizationId.value = null
      localStorage.removeItem('defaultOrganizationId')
    } catch (error) {
      console.error('Error removing default organization:', error)
      throw error
    }
  }

  // Carregadores de dados específicos da organização
  const loadCostCenters = async () => {
    if (!organizationId.value) return
    const q = query(
      collection(db, 'cost_centers'),
      where('organizationId', '==', organizationId.value)
    )
    const snapshot = await getDocs(q)
    costCenters.value = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
  }

  const loadCustomers = async () => {
    if (!organizationId.value) return
    const q = query(
      collection(db, 'customers'),
      where('organizationId', '==', organizationId.value)
    )
    const snapshot = await getDocs(q)
    customers.value = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
  }

  const loadSuppliers = async () => {
    if (!organizationId.value) return
    const q = query(
      collection(db, 'suppliers'),
      where('organizationId', '==', organizationId.value)
    )
    const snapshot = await getDocs(q)
    suppliers.value = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
  }

  const loadBanks = async () => {
    if (!organizationId.value) return
    const q = query(
      collection(db, 'banks'),
      where('organizationId', '==', organizationId.value)
    )
    const snapshot = await getDocs(q)
    banks.value = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
  }

  const loadCashFlow = async () => {
    if (!organizationId.value) return
    const q = query(
      collection(db, 'cash_flow'),
      where('organizationId', '==', organizationId.value)
    )
    const snapshot = await getDocs(q)
    cashFlow.value = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
  }

  const loadAccountsPayable = async () => {
    if (!organizationId.value) return

    try {
      const q = query(
        collection(db, 'accounts_payable'),
        where('organizationId', '==', organizationId.value),
        where('deletedAt', '==', null)
      )
      const snapshot = await getDocs(q)
      accountsPayable.value = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        amount: doc.data().value || 0,
        dueDate: doc.data().dueDate || null,
        status: doc.data().status || 'PENDENTE'
      }))
    } catch (error) {
      console.error('Error loading accounts payable:', error)
      accountsPayable.value = []
    }
  }

  const loadAccountsReceivable = async () => {
    if (!organizationId.value) return

    try {
      const q = query(
        collection(db, 'accounts_receivable'),
        where('organizationId', '==', organizationId.value),
        where('deletedAt', '==', null)
      )
      const snapshot = await getDocs(q)
      accountsReceivable.value = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        amount: doc.data().value || 0,
        dueDate: doc.data().dueDate || null,
        status: doc.data().status || 'PENDENTE'
      }))
    } catch (error) {
      console.error('Error loading accounts receivable:', error)
      accountsReceivable.value = []
    }
  }

  const loadDashboard = async () => {
    if (!organizationId.value) return
    const docRef = doc(db, 'dashboards', organizationId.value)
    const docSnap = await getDoc(docRef)
    if (docSnap.exists()) {
      dashboardData.value = {
        id: docSnap.id,
        ...docSnap.data()
      }
    } else {
      dashboardData.value = null
    }
  }

  const loadSavedOrganization = async () => {
    try {
      const savedOrgId = localStorage.getItem('currentOrganizationId')
      if (!savedOrgId) return null

      const orgDoc = await getDoc(doc(db, 'organizations', savedOrgId))
      if (!orgDoc.exists()) {
        localStorage.removeItem('currentOrganizationId')
        return null
      }

      const org = { id: orgDoc.id, ...orgDoc.data() } as Organization
      await setCurrentOrganization(org)
      return org
    } catch (error) {
      console.error('Error loading saved organization:', error)
      return null
    }
  }

  // Carrega a organização padrão do usuário
  const loadDefaultOrganization = async () => {
    try {
      // Se já tiver uma organização salva, usa ela
      const savedOrg = await loadSavedOrganization()
      if (savedOrg) return savedOrg

      // Se não tiver salva, tenta carregar a padrão
      const savedOrgId = localStorage.getItem('defaultOrganizationId')
      if (!savedOrgId) return null

      const orgDoc = await getDoc(doc(db, 'organizations', savedOrgId))
      if (!orgDoc.exists()) {
        localStorage.removeItem('defaultOrganizationId')
        return null
      }

      const org = { id: orgDoc.id, ...orgDoc.data() } as Organization
      await setCurrentOrganization(org)
      return org
    } catch (error) {
      console.error('Error loading default organization:', error)
      return null
    }
  }

  // Limpa todos os dados quando o usuário faz logout
  const clearData = () => {
    currentOrganization.value = null
    defaultOrganizationId.value = null
    costCenters.value = []
    customers.value = []
    suppliers.value = []
    banks.value = []
    cashFlow.value = []
    accountsPayable.value = []
    accountsReceivable.value = []
    dashboardData.value = null
    localStorage.removeItem('currentOrganizationId')
    localStorage.removeItem('defaultOrganizationId')
  }

  return {
    // State
    currentOrganization,
    defaultOrganizationId,
    isLoading,
    costCenters,
    customers,
    suppliers,
    banks,
    cashFlow,
    accountsPayable,
    accountsReceivable,
    dashboardData,

    // Getters
    organizationId,

    // Actions
    setCurrentOrganization,
    setDefaultOrganization,
    removeDefaultOrganization,
    loadCostCenters,
    loadCustomers,
    loadSuppliers,
    loadBanks,
    loadCashFlow,
    loadAccountsPayable,
    loadAccountsReceivable,
    loadDashboard,
    clearData,
    loadDefaultOrganization,
    loadSavedOrganization
  }
})
